import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Link2, Grid3X3, List, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Requirement {
  id: string;
  projectId: string;
  requirementNumber: string;
  title: string;
  description: string | null;
  category: string;
  priority: string;
  status: string;
  source: string | null;
  acceptanceCriteria: string | null;
  phaseName: string | null;
  linkedTaskCount: number;
  linkedRiskCount: number;
  linkedDeliverableCount: number;
}

interface TraceabilityRow {
  requirementId: string;
  requirementNumber: string;
  requirementTitle: string;
  requirementStatus: string;
  requirementCategory: string;
  requirementPriority: string;
  coverageStatus: string;
  linkedTasks: Array<{ id: string; title: string; status: string }>;
  linkedRisks: Array<{ id: string; title: string; riskScore: number; status: string }>;
  linkedDeliverables: Array<{ id: string; name: string; status: string }>;
}

interface Project {
  id: string;
  name: string;
}

const categoryLabels: Record<string, string> = {
  functional: 'Functional',
  non_functional: 'Non-Functional',
  business: 'Business',
  technical: 'Technical',
  compliance: 'Compliance',
  security: 'Security',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  approved: 'Approved',
  in_progress: 'In Progress',
  implemented: 'Implemented',
  verified: 'Verified',
  deferred: 'Deferred',
  rejected: 'Rejected',
};

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  draft: 'secondary',
  approved: 'info',
  in_progress: 'default',
  implemented: 'success',
  verified: 'success',
  deferred: 'warning',
  rejected: 'destructive',
};

const coverageVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  covered: 'success',
  partial: 'warning',
  uncovered: 'destructive',
};

const priorityVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  low: 'secondary',
  medium: 'default',
  high: 'warning',
  urgent: 'destructive',
};

export function RequirementsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const initialProject = searchParams.get('project') || '';
  const [view, setView] = useState<'list' | 'matrix'>('list');
  const [selectedProjectId, setSelectedProjectId] = useState<string>(initialProject);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [linkReqId, setLinkReqId] = useState<string>('');
  const [form, setForm] = useState({
    projectId: '',
    requirementNumber: '',
    title: '',
    description: '',
    category: 'functional',
    priority: 'medium',
    source: '',
    acceptanceCriteria: '',
  });
  const [linkForm, setLinkForm] = useState({
    taskId: '',
    riskId: '',
    deliverableId: '',
    linkType: 'implements',
    notes: '',
  });

  // Fetch projects for dropdown
  const { data: projectsData } = useQuery({
    queryKey: ['projects-dropdown'],
    queryFn: async () => {
      const r = await api.listProjects({ limit: 100 });
      return (r.data || []) as Project[];
    },
  });
  const projects = projectsData || [];

  // Fetch requirements list
  const { data: reqData, isLoading } = useQuery({
    queryKey: ['requirements', selectedProjectId, categoryFilter, statusFilter, search],
    queryFn: async () => {
      const r = await api.listRequirements({
        projectId: selectedProjectId || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
        limit: 100,
      });
      return (r.data || []) as Requirement[];
    },
  });
  const requirements = reqData || [];

  // Fetch traceability matrix
  const { data: matrixData } = useQuery({
    queryKey: ['traceability-matrix', selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return [];
      const r = await api.getTraceabilityMatrix(selectedProjectId);
      return (r.data || []) as TraceabilityRow[];
    },
    enabled: view === 'matrix' && !!selectedProjectId,
  });
  const matrix = matrixData || [];

  // Fetch tasks/risks for linking
  const { data: tasksData } = useQuery({
    queryKey: ['project-tasks-for-link', selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return [];
      const r = await api.listProjectTasks({ projectId: selectedProjectId, limit: 200 });
      return (r.data || []) as Array<{ id: string; title: string }>;
    },
    enabled: showLink && !!selectedProjectId,
  });

  const { data: risksData } = useQuery({
    queryKey: ['project-risks-for-link', selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return [];
      const r = await api.listRisks({ projectId: selectedProjectId, limit: 200 });
      return (r.data || []) as Array<{ id: string; title: string }>;
    },
    enabled: showLink && !!selectedProjectId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createRequirement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['traceability-matrix'] });
      setShowCreate(false);
      toast({ title: 'Requirement created' });
    },
    onError: () => toast({ title: 'Failed to create requirement', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteRequirement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['traceability-matrix'] });
      toast({ title: 'Requirement deleted' });
    },
  });

  const linkMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createRequirementLink(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['traceability-matrix'] });
      setShowLink(false);
      toast({ title: 'Link created' });
    },
    onError: () => toast({ title: 'Failed to create link', variant: 'destructive' }),
  });

  // unlinkMutation can be enabled when unlink UI is added
  // const unlinkMutation = useMutation({
  //   mutationFn: (linkId: string) => api.deleteRequirementLink(linkId),
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: ['traceability-matrix'] });
  //     toast({ title: 'Link removed' });
  //   },
  // });

  const handleCreate = () => {
    createMutation.mutate({
      projectId: form.projectId || selectedProjectId,
      requirementNumber: form.requirementNumber,
      title: form.title,
      description: form.description || null,
      category: form.category,
      priority: form.priority,
      source: form.source || null,
      acceptanceCriteria: form.acceptanceCriteria || null,
    });
  };

  const handleLink = () => {
    linkMutation.mutate({
      requirementId: linkReqId,
      taskId: linkForm.taskId || null,
      riskId: linkForm.riskId || null,
      deliverableId: linkForm.deliverableId || null,
      linkType: linkForm.linkType,
      notes: linkForm.notes || null,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Requirements & Traceability</h1>
          <p className="text-muted-foreground">Track requirements and their links to tasks, risks, and deliverables</p>
        </div>
        <Button onClick={() => { setForm({ projectId: selectedProjectId, requirementNumber: '', title: '', description: '', category: 'functional', priority: 'medium', source: '', acceptanceCriteria: '' }); setShowCreate(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Requirement
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={selectedProjectId || '__all__'} onValueChange={(v) => setSelectedProjectId(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Projects</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryFilter || '__all__'} onValueChange={(v) => setCategoryFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Categories</SelectItem>
            {Object.entries(categoryLabels).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter || '__all__'} onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            {Object.entries(statusLabels).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-[200px]" />
        <div className="ml-auto">
          <Tabs value={view} onValueChange={(v: string) => setView(v as 'list' | 'matrix')}>
            <TabsList>
              <TabsTrigger value="list"><List className="h-4 w-4 mr-1" /> List</TabsTrigger>
              <TabsTrigger value="matrix" disabled={!selectedProjectId}><Grid3X3 className="h-4 w-4 mr-1" /> RTM</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* List View */}
      {view === 'list' && (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : requirements.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No requirements found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">ID</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Tasks</TableHead>
                    <TableHead className="text-center">Risks</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requirements.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell className="font-mono text-xs">{req.requirementNumber}</TableCell>
                      <TableCell>
                        <div className="font-medium">{req.title}</div>
                        {req.source && <div className="text-xs text-muted-foreground">Source: {req.source}</div>}
                      </TableCell>
                      <TableCell><Badge variant="secondary">{categoryLabels[req.category] || req.category}</Badge></TableCell>
                      <TableCell><Badge variant={priorityVariant[req.priority] || 'secondary'}>{req.priority}</Badge></TableCell>
                      <TableCell><Badge variant={statusVariant[req.status] || 'secondary'}>{statusLabels[req.status] || req.status}</Badge></TableCell>
                      <TableCell className="text-center">{req.linkedTaskCount}</TableCell>
                      <TableCell className="text-center">{req.linkedRiskCount}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setLinkReqId(req.id); setLinkForm({ taskId: '', riskId: '', deliverableId: '', linkType: 'implements', notes: '' }); setShowLink(true); }}>
                            <Link2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(req.id)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Traceability Matrix View */}
      {view === 'matrix' && selectedProjectId && (
        <Card>
          <CardHeader>
            <CardTitle>Requirements Traceability Matrix</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {matrix.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No requirements found for this project</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Req ID</TableHead>
                    <TableHead>Requirement</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Coverage</TableHead>
                    <TableHead>Linked Tasks</TableHead>
                    <TableHead>Linked Risks</TableHead>
                    <TableHead>Deliverables</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matrix.map((row) => (
                    <TableRow key={row.requirementId}>
                      <TableCell className="font-mono text-xs">{row.requirementNumber}</TableCell>
                      <TableCell>
                        <div className="font-medium">{row.requirementTitle}</div>
                        <Badge variant="secondary" className="text-[10px] mt-0.5">{categoryLabels[row.requirementCategory]}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant[row.requirementStatus] || 'secondary'}>
                          {statusLabels[row.requirementStatus] || row.requirementStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={coverageVariant[row.coverageStatus] || 'secondary'}>
                          {row.coverageStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {row.linkedTasks.length === 0 ? (
                          <span className="text-xs text-muted-foreground">None</span>
                        ) : (
                          <div className="space-y-1">
                            {row.linkedTasks.map((t) => (
                              <div key={t.id} className="text-xs flex items-center gap-1">
                                <Badge variant={t.status === 'done' ? 'success' : 'secondary'} className="text-[10px]">
                                  {t.status}
                                </Badge>
                                <span className="truncate max-w-[150px]">{t.title}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.linkedRisks.length === 0 ? (
                          <span className="text-xs text-muted-foreground">None</span>
                        ) : (
                          <div className="space-y-1">
                            {row.linkedRisks.map((r) => (
                              <div key={r.id} className="text-xs flex items-center gap-1">
                                <Badge variant={r.riskScore >= 12 ? 'destructive' : r.riskScore >= 6 ? 'warning' : 'secondary'} className="text-[10px]">
                                  {r.riskScore}
                                </Badge>
                                <span className="truncate max-w-[150px]">{r.title}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.linkedDeliverables.length === 0 ? (
                          <span className="text-xs text-muted-foreground">None</span>
                        ) : (
                          <div className="space-y-1">
                            {row.linkedDeliverables.map((d) => (
                              <div key={d.id} className="text-xs flex items-center gap-1">
                                <Badge variant={d.status === 'approved' ? 'success' : 'secondary'} className="text-[10px]">
                                  {d.status}
                                </Badge>
                                <span className="truncate max-w-[150px]">{d.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {view === 'matrix' && !selectedProjectId && (
        <Card>
          <CardContent className="text-center py-12 text-muted-foreground">
            Select a project to view the Traceability Matrix
          </CardContent>
        </Card>
      )}

      {/* Create Requirement Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Requirement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Project</Label>
              <Select value={form.projectId} onValueChange={(v) => setForm({ ...form, projectId: v })}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Requirement #</Label>
                <Input value={form.requirementNumber} onChange={(e) => setForm({ ...form, requirementNumber: e.target.value })} placeholder="REQ-001" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source</Label>
                <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Client, SOW, etc." />
              </div>
            </div>
            <div>
              <Label>Acceptance Criteria</Label>
              <Textarea value={form.acceptanceCriteria} onChange={(e) => setForm({ ...form, acceptanceCriteria: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!form.projectId || !form.requirementNumber || !form.title}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Requirement Dialog */}
      <Dialog open={showLink} onOpenChange={setShowLink}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Requirement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Link Type</Label>
              <Select value={linkForm.linkType} onValueChange={(v) => setLinkForm({ ...linkForm, linkType: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="implements">Implements (Task)</SelectItem>
                  <SelectItem value="tests">Tests (Task)</SelectItem>
                  <SelectItem value="mitigates">Mitigates (Risk)</SelectItem>
                  <SelectItem value="validates">Validates (Deliverable)</SelectItem>
                  <SelectItem value="depends_on">Depends On</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(linkForm.linkType === 'implements' || linkForm.linkType === 'tests' || linkForm.linkType === 'depends_on') && (
              <div>
                <Label>Task</Label>
                <Select value={linkForm.taskId} onValueChange={(v) => setLinkForm({ ...linkForm, taskId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select task" /></SelectTrigger>
                  <SelectContent>
                    {(tasksData || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {linkForm.linkType === 'mitigates' && (
              <div>
                <Label>Risk</Label>
                <Select value={linkForm.riskId} onValueChange={(v) => setLinkForm({ ...linkForm, riskId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select risk" /></SelectTrigger>
                  <SelectContent>
                    {(risksData || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Input value={linkForm.notes} onChange={(e) => setLinkForm({ ...linkForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLink(false)}>Cancel</Button>
            <Button onClick={handleLink} disabled={!linkForm.taskId && !linkForm.riskId && !linkForm.deliverableId}>
              <Link2 className="h-4 w-4 mr-2" />
              Create Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
